import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
// Import DIRETO, nao pelo barrel `../auth`: o barrel passou a exportar `useOrgRole`, que
// importa este arquivo. Pelo barrel isso fecharia um ciclo (auth -> useOrgRole ->
// usePermissions -> auth). Hoje ele nao quebraria, porque tudo aqui e funcao usada em
// render e nao no topo do modulo — mas e sorte, nao desenho.
import { useCurrentOrgId } from '../auth/useCurrentOrgId';
import { queryConfigs } from '@/lib/queryClient';
import type { PermissionKey } from '@/lib/permissions/keys';

export type RecordScope = 'own' | 'team' | 'squad' | 'hierarchy' | 'all';

export interface EffectiveAccess {
  org_id: string;
  is_super_admin: boolean;
  role: string | null;
  record_scope: RecordScope;
  groups: { id: string; name: string; record_scope: RecordScope; from_template: boolean }[];
  permissions: string[];
  perms_version: string;
}

const VAZIO: EffectiveAccess = {
  org_id: '', is_super_admin: false, role: null, record_scope: 'own',
  groups: [], permissions: [], perms_version: '',
};

const CHAVE_VERSAO = 'vertto.permsVersao';

/**
 * Acesso efetivo do usuário na org corrente.
 *
 * ── Uma chamada, não três ──
 * `my_effective_access(org)` devolve papel, escopo, grupos, chaves e o carimbo de versão de uma
 * vez. Ler as 3 tabelas por PostgREST exigiria embeds, e embed neste repo depende do schema
 * cache — que fica velho depois de DDL manual e responde 503 (é DDL manual que aplica tudo aqui,
 * ver Regra 4). Uma RPC não tem esse acoplamento.
 *
 * ── Por que `session` mas com refetch no foco ──
 * A categoria Sessão (`staleTime: Infinity`) é a certa: permissão não muda no meio de um
 * clique. Mas o refetch no foco é ligado de propósito — é o que faz a sidebar de quem TEVE a
 * permissão alterada se ajustar ao voltar para a aba, sem F5. Sem ele, o admin edita a matriz e
 * a pessoa do outro lado continua vendo o menu antigo até recarregar.
 *
 * ── O purge por `perms_version` ──
 * Esconder o item do menu não basta: o React Query tem `gcTime` de 30min (24h no caso do
 * default com stale-while-revalidate), então a TELA que a pessoa acabou de perder continuaria
 * servindo do cache se ela navegasse por um link direto. Quando o carimbo muda, tudo que não é
 * permissão sai do cache. O carimbo vem do servidor em ordem de catálogo — ordem instável faria
 * ele oscilar e purgar o cache à toa.
 */
export function usePermissions() {
  const { orgId } = useCurrentOrgId();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    ...queryConfigs.session,
    refetchOnWindowFocus: true,
    queryKey: ['permissions', orgId],
    enabled: Boolean(orgId),
    queryFn: async (): Promise<EffectiveAccess> => {
      if (!orgId) return VAZIO;
      const { data, error } = await supabase.rpc('my_effective_access' as never, {
        p_org_id: orgId,
      } as never);
      if (error) throw error;
      return (data as unknown as EffectiveAccess) ?? VAZIO;
    },
  });

  const versao = data?.perms_version ?? null;
  const purgadoRef = useRef<string | null>(null);

  useEffect(() => {
    if (!versao || purgadoRef.current === versao) return;
    const anterior = sessionStorage.getItem(CHAVE_VERSAO);
    if (anterior && anterior !== versao) {
      // Só o que NÃO é permissão: purgar a própria query nos deixaria em loop de refetch.
      queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== 'permissions' });
    }
    sessionStorage.setItem(CHAVE_VERSAO, versao);
    purgadoRef.current = versao;
  }, [versao, queryClient]);

  const chaves = useMemo(() => new Set(data?.permissions ?? []), [data?.permissions]);

  return {
    loading: isLoading,
    error: error as Error | null,
    access: data ?? VAZIO,
    permissions: chaves,
    recordScope: (data?.record_scope ?? 'own') as RecordScope,
    groups: data?.groups ?? [],
    isSuperAdmin: data?.is_super_admin ?? false,
    role: data?.role ?? null,
    /**
     * `null`/`undefined` devolve TRUE de propósito: é o caso "rota sem chave declarada".
     * No v1 a Sidebar é ADITIVA (consulta permissões em cima da escada, não no lugar dela), e
     * 92 das ~110 rotas do router não têm chave. Negar por omissão aqui apagaria a maior parte
     * do app. O fail-closed por omissão entra junto com a Fase 3, quando o catálogo cobrir o
     * que precisa cobrir e a escada sair.
     */
    has: (key?: PermissionKey | string | null) => (key ? chaves.has(key) : true),
  };
}

/** Açúcar para um `if` só. Mesma semântica de `has`. */
export function useHasPermission(key?: PermissionKey | string | null): boolean {
  return usePermissions().has(key);
}
